/**
 * Multi-firma workspace registry – localStorage‑based.
 * Predvolená firma (id: null) nie je nikdy uložená v úložisku, vždy sa generuje in-memory.
 *
 * Tento modul NESMIE importovať nič z engine.js / driveSync.js / db.js / UI.
 * Ostatné vrstvy ho budú používať ako závislosť v neskorších balíkoch.
 */
const STORAGE_KEY_WORKSPACES = 'todue_workspaces_v1';
const STORAGE_KEY_ACTIVE_ID = 'todue_active_workspace_id';

const DEFAULT_WORKSPACE = Object.freeze({
  id: null,
  name: 'Predvolená firma',
});

/* ── pomocné funkcie ───────────────────────────────────────── */

/**
 * Načíta zoznam pridaných firiem z localStorage.
 * Vracia pole objektov {id, name}, nikdy nie null a garantuje pole (aj keď úložisko
 * obsahuje neplatnú hodnotu, napr. reťazec "null").
 *
 * Všetky neplatné položky sú preskočené – vrátené pole obsahuje IBA platné firmy.
 * Predvolená firma (id === null) je zo zoznamu odstránená (NESMIE byť v úložisku).
 */
function parseStoredWorkspaces(raw) {
  if (!raw) return [];
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return []; // poškodený JSON — obsah je nečitateľný, nie chyba úložiska
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter(
      (item) =>
        item !== null &&
        typeof item === 'object' &&
        'id' in item &&
        'name' in item &&
        // Cross-family verify (Codex, kolo 3, 2026-08-05): `typeof item.id === 'string'` je
        // BLOCKING, nie kozmetika — id=1 (number) a id='1' (string) by po String() koerzii
        // v dbNameForWorkspace/drivePrefixForWorkspace dali IDENTICKÝ výsledok, zatiaľ čo
        // registry lookup (`===`) by ich považoval za DVE rôzne firmy → kolízia DB/Drive
        // namespace. Táto validácia na vstupnej hranici zaručí, že takýto záznam sa do
        // dôveryhodného registra nikdy nedostane (ani z poškodeného/ručne upraveného úložiska).
        typeof item.id === 'string' &&
        item.id !== '' &&
        item.id !== null // predvolená firma nikdy nepatrí do uloženého poľa
    )
    // type/ico/card_color — voliteľné metadáta (Nastavenia dizajn audit 2026-08-08, peek
    // karta v Nastaveniach ich potrebuje na zobrazenie neaktívnej firmy). Staré uložené
    // záznamy {id,name} bez týchto polí ostávajú platné (undefined, nie chyba) — appka
    // s nimi žila predtým, nesmie na ne padnúť ani po tejto zmene.
    .map((item) => ({
      id: item.id,
      name: item.name,
      ...(typeof item.type === 'string' ? { type: item.type } : {}),
      ...(typeof item.ico === 'string' ? { ico: item.ico } : {}),
      ...(typeof item.card_color === 'string' ? { card_color: item.card_color } : {}),
    }));
}

/**
 * Číta zoznam pridaných firiem — LENIENT variant pre read-only zobrazenie
 * (listWorkspaces/getActiveWorkspace). Akákoľvek chyba (nedostupné úložisko AJ
 * poškodený obsah) sa tíško premení na prázdny zoznam — UI nesmie spadnúť len
 * preto, že si chce zobraziť zoznam firiem.
 */
function loadStoredWorkspaces() {
  try {
    return parseStoredWorkspaces(localStorage.getItem(STORAGE_KEY_WORKSPACES));
  } catch {
    return [];
  }
}

/**
 * Číta zoznam pridaných firiem — STRICT variant pre MUTÁCIE (addWorkspace/
 * renameWorkspace). Cross-family verify (Codex, 2026-08-05) našiel: lenient čítanie
 * v mutačnej ceste je nebezpečné — ak `getItem()` zlyhá (prechodná SecurityError a
 * pod.) ale `setItem()` napriek tomu funguje, `addWorkspace` by TICHO PREPÍSAL celý
 * register len novo pridanou firmou (read vrátil [], write [] + entry uložil ako
 * jediný záznam) → nenávratná strata všetkých existujúcich firiem. Táto verzia
 * rozlišuje "úložisko nedostupné" (hodí typovanú chybu, mutácia sa NEVYKONÁ) od
 * "obsah je prázdny/poškodený" (bezpečne prázdne pole, mutácia pokračuje).
 */
function loadStoredWorkspacesStrict() {
  let raw;
  try {
    raw = localStorage.getItem(STORAGE_KEY_WORKSPACES);
  } catch (e) {
    const err = new Error('Nepodarilo sa prečítať zoznam firiem — úložisko je nedostupné.');
    err.code = 'E_WORKSPACE_STORAGE_UNAVAILABLE';
    err.cause = e;
    throw err;
  }
  return parseStoredWorkspaces(raw);
}

/**
 * Uloží pole pridaných firiem do localStorage.
 *
 * NEtlmí chybu potichu (na rozdiel od getActiveWorkspaceId/setActiveWorkspaceId) — silné
 * "úspech" na addWorkspace/renameWorkspace by bol falošný, keby sa zápis reálne nepodaril
 * (quota exceeded / private mode / zakázané úložisko): user by veril že firmu vytvoril,
 * po reloade by zmizla. Namiesto surovej DOMException hodí čitateľnú, typovanú chybu,
 * ktorú UI vrstva (neskorší balík) vie odchytiť a ukázať používateľovi.
 */
function saveStoredWorkspaces(list) {
  try {
    localStorage.setItem(STORAGE_KEY_WORKSPACES, JSON.stringify(list));
  } catch (e) {
    const err = new Error('Nepodarilo sa uložiť firmu — úložisko je plné alebo nedostupné.');
    err.code = 'E_WORKSPACE_STORAGE_UNAVAILABLE';
    err.cause = e;
    throw err;
  }
}

/**
 * Vygeneruje unikátny identifikátor pre workspace.
 * V prostredí bez crypto.randomUUID() (zastarané prehliadače/testy) fallback na
 * Date.now() + Math.random(), čo je pre tento účel postačujúce.
 */
function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // fallback – v praxi sa nepoužije, slúži len na pokrytie zvláštnych prostredí
  return `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
}

/**
 * Vygeneruje id, ktoré ešte NIE JE v `existingIds` — UUID kolízia je astronomicky
 * nepravdepodobná, ale fallback generátor (bez crypto.randomUUID) ju teoreticky
 * pripúšťa (Codex cross-family verify, 2026-08-05: "duplicate immediately collapses
 * two companies into the same DB and Drive namespace"). Pár tichých retry, potom
 * explicitný throw — NIKDY nevráti id, ktoré už niekto používa.
 */
function generateUniqueId(existingIds) {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const id = generateId();
    if (!existingIds.has(id)) return id;
  }
  const err = new Error('Nepodarilo sa vygenerovať unikátne ID firmy — skús to znova.');
  err.code = 'E_WORKSPACE_ID_COLLISION';
  throw err;
}

/* ── verejné API ───────────────────────────────────────────── */

/**
 * Vráti zoznam všetkých existujúcich firiem.
 * Predvolená firma je vždy na prvom mieste, za ňou nasledujú používateľom pridané firmy.
 */
export function listWorkspaces() {
  const stored = loadStoredWorkspaces();
  return [DEFAULT_WORKSPACE, ...stored];
}

/**
 * Pridá novú firmu s daným menom.
 * @param {string} name
 * @returns {{id: string, name: string}}
 */
export function addWorkspace(name) {
  const stored = loadStoredWorkspacesStrict();
  const id = generateUniqueId(new Set(stored.map((ws) => ws.id)));
  const entry = { id, name };

  stored.push(entry);
  saveStoredWorkspaces(stored);

  return entry;
}

/**
 * Zapíše/aktualizuje metadáta firmy (type/ico/card_color) — volá SettingsV2 pri uložení
 * profilu/dizajnu AKTÍVNEJ firmy, aby peek karta (Nastavenia wallet stack) mala čo
 * zobraziť aj keď táto firma prestane byť aktívna. Predvolená firma (id===null) sa
 * NEUKLADÁ (rovnaký zámer ako renameWorkspace) — je vždy in-memory.
 * Ak firma s daným ID neexistuje v registri, volanie nevykoná nič (no-op) — SettingsV2
 * ju nemôže volať pre firmu, ktorá tam ešte nie je (napr. legacy dáta pred touto zmenou).
 * @param {string|null} id
 * @param {{type?: string, ico?: string, card_color?: string}} patch
 */
export function updateWorkspaceMeta(id, patch) {
  if (id === null) return;
  const stored = loadStoredWorkspacesStrict();
  const index = stored.findIndex((ws) => ws.id === id);
  if (index === -1) return;

  const next = { ...stored[index] };
  if (typeof patch?.type === 'string') next.type = patch.type;
  if (typeof patch?.ico === 'string') next.ico = patch.ico;
  if (typeof patch?.card_color === 'string') next.card_color = patch.card_color;
  stored[index] = next;
  saveStoredWorkspaces(stored);
}

/**
 * Premenuje existujúcu firmu.
 * Ak firma s daným ID neexistuje, volanie nevykoná nič (no-op).
 * @param {string} id
 * @param {string} name
 */
export function renameWorkspace(id, name) {
  if (id === null) {
    // predvolenú firmu nepremenúvame, je len in-memory
    return;
  }

  const stored = loadStoredWorkspacesStrict();
  const index = stored.findIndex((ws) => ws.id === id);
  if (index === -1) return;

  stored[index] = { ...stored[index], name };
  saveStoredWorkspaces(stored);
}

/**
 * Získa ID aktívnej firmy z localStorage.
 * Pri akejkoľvek chybe (napr. localStorage nie je k dispozícii) vráti null.
 * @returns {string | null}
 */
export function getActiveWorkspaceId() {
  try {
    return localStorage.getItem(STORAGE_KEY_ACTIVE_ID) || null;
  } catch {
    return null;
  }
}

/**
 * Nastaví ID aktívnej firmy do localStorage.
 * Ak id === null, odstráni kľúč, čím sa implicitne aktivuje predvolená firma.
 * V prípade chyby (nedostupné localStorage) sa volanie potichu ignoruje.
 * @param {string|null} id
 */
export function setActiveWorkspaceId(id) {
  try {
    if (id === null) {
      localStorage.removeItem(STORAGE_KEY_ACTIVE_ID);
    } else {
      localStorage.setItem(STORAGE_KEY_ACTIVE_ID, id);
    }
  } catch {
    // localStorage nie je k dispozícii – nemožno zapísať, stav ostáva nezmenený
  }
}

/**
 * Vráti objekt aktívnej firmy (id, name).
 * Ak ID nie je nastavené alebo neexistuje v registri, vráti bezpečne predvolenú firmu.
 * @returns {{id: string|null, name: string}}
 */
export function getActiveWorkspace() {
  const activeId = getActiveWorkspaceId();
  if (!activeId) return DEFAULT_WORKSPACE;

  const stored = loadStoredWorkspaces();
  const found = stored.find((ws) => ws.id === activeId);
  return found ? found : DEFAULT_WORKSPACE;
}

/**
 * Vráti DB názov pre daný workspace.
 * Legácy predvolený workspace (id === null) používa presne 'todue_v2',
 * všetky ostatné dostávajú meno v tvare `todue_v2_ws_<id>`.
 * @param {string|null} id
 * @returns {string}
 */
export function dbNameForWorkspace(id) {
  if (id === null) return 'todue_v2';
  return `todue_v2_ws_${id}`;
}

/**
 * Vráti Drive prefix pre daný workspace a základný prefix.
 * Pre predvolený workspace sa vráti rovnaký basePrefix (žiadna zmena),
 * inak sa prefixuje reťazcom v tvare `ws<dĺžka id>:<id>-<basePrefix>`.
 *
 * Cross-family verify (Codex, 2026-08-05) chytil: prostý `ws${id}-` NIE JE
 * prefix-free pre ĽUBOVOĽNÝ obsah id (napr. id="a" dá "wsa-record-", id="a-record-b"
 * dá "wsa-record-b-record-" — prvý reťazec je prefixom druhého → Drive `startsWith`
 * filter by pretiekol dáta medzi firmami). Skutočné id z `generateUniqueId` (UUID,
 * fixná dĺžka) toto v praxi nevyrobí, ale funkcia je verejná a nesmie sa spoliehať
 * len na to, ako ju dnes volajú. Netstring-štýl (dĺžka id pred oddeľovačom `:`)
 * garantuje prefix-free vlastnosť pre AKÝKOĽVEK vstup — dva rôzne (dĺžka, obsah)
 * páry nemôžu byť navzájom prefixom, lebo dĺžka je zakódovaná pred oddeľovačom,
 * ktorý sa v číslici nemôže objaviť.
 *
 * @param {string|null} id
 * @param {string} basePrefix – napr. 'record-' (z engine/driveSync)
 * @returns {string}
 */
export function drivePrefixForWorkspace(id, basePrefix) {
  if (id === null) return basePrefix;
  // Obranná hranica (nezávislá od parseStoredWorkspaces validácie vyššie): netstring
  // enkódovanie je prefix-free len ak `id` je VŽDY typu string — číslo 1 a reťazec '1'
  // by po String() koerzii dali identický výsledok (Codex kolo 3). Radšej hlasný throw
  // než tichá kolízia dvoch firiem do jedného Drive namespace.
  if (typeof id !== 'string' || id === '') {
    const err = new Error('drivePrefixForWorkspace: id musí byť neprázdny string.');
    err.code = 'E_WORKSPACE_INVALID_ID';
    throw err;
  }
  return `ws${id.length}:${id}-${basePrefix}`;
}
